// The three outbound dialogs: "Which desk is this computer?" (opened from My desk, the
// call pill or a Call button; the sign-in prompt is DeskPromptPill), the desk clash
// ("R TCO 1 is in use by Sam"), and the handset fallback when the desk isn't
// connected to the CRM yet.
import PropTypes from 'prop-types';
import { useState } from 'react';
import {
    Button, Dialog, DialogActions, DialogContent, DialogTitle, List, ListItemButton, ListItemText, Stack, Typography,
} from '@mui/material';
import { CONNECTION, deskLabel, heldByName, heldBySomeoneElse, pickerSections } from './outboundModel';
import { callLayer } from '../callPill/callLayers';

// Opened from the call pill or the dialer, which float above MUI's normal layers: these
// dialogs go above them (callLayers.js), or they render underneath and can't be clicked.
const dialogSx = { zIndex: callLayer('DIALOG') };

export function DeskPickerDialog({ open, desks, currentDeskId, heldDeskId, usualDeskId, lastDeskId, showCallSetup, onPick, onRelease, onClose }) {
    // The handset last used here comes first (shared setups are asked on Call, not each morning),
    // then the admin-set usual desk, each marked, so either is a one-click pick (CU-869fmd1vf).
    const rank = (d) => {
        if (String(d.id) === String(lastDeskId)) return 0;
        return String(d.id) === String(usualDeskId) ? 1 : 2;
    };
    const ordered = lastDeskId || usualDeskId ? [...desks].sort((a, b) => rank(a) - rank(b)) : desks;
    // Handsets shared with me first, the rest folded under "Other desks" (pickerSections); a
    // flat list, as before, when nothing is shared with me.
    const [showOther, setShowOther] = useState(false);
    const sections = pickerSections(ordered);
    const mine = sections.find((section) => section.key === 'shared');
    const other = mine ? sections.find((section) => section.key === 'other') : null;
    const visible = mine ? [...mine.desks, ...(other && showOther ? other.desks : [])] : ordered;
    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth sx={dialogSx}>
            <DialogTitle>Which desk is this computer?</DialogTitle>
            <DialogContent dividers sx={{ p: 0 }}>
                <Typography variant="body2" sx={{ px: 3, py: 1.5, color: 'text.secondary' }}>
                    Calls to this desk phone pop up for you here, and calls you start ring it first. Change it any time from Call Logs, the call pill or your account menu.
                </Typography>
                {desks.length === 0 ? (
                    <Typography variant="body2" sx={{ px: 3, pb: 2 }}>No desks are set up yet. Ask an admin to set up desks in Phone system settings.</Typography>
                ) : (
                    <List dense disablePadding data-testid="desk-picker-list">
                        {visible.map((d) => {
                            const heldBy = heldBySomeoneElse(d);
                            return (
                                <ListItemButton
                                    key={d.id}
                                    selected={String(d.id) === String(currentDeskId)}
                                    onClick={() => onPick(d)}
                                    data-testid={`desk-option-${d.id}`}
                                >
                                    <ListItemText
                                        primary={deskLabel(d)}
                                        secondary={[
                                            d.extension && `Ext ${d.extension}`,
                                            String(d.id) === String(heldDeskId) && 'You\'re here',
                                            String(d.id) === String(usualDeskId) && 'Your usual desk',
                                            String(d.id) === String(lastDeskId) && String(d.id) !== String(heldDeskId) && 'Last used',
                                            heldBy && `In use by ${heldByName(heldBy)}`,
                                            d.held_elsewhere && 'In use at another practice',
                                            // Picking a desk never needs desk sign-in: that's only for starting calls
                                            // from the CRM, so it's a small hint, and only for people who can (CU-869fmcc9r).
                                            showCallSetup && d.connection_state !== CONNECTION.CONNECTED && 'Calls from CRM: not set up',
                                        ].filter(Boolean).join(' · ')}
                                    />
                                </ListItemButton>
                            );
                        })}
                        {other && (
                            <ListItemButton onClick={() => setShowOther((v) => !v)} aria-expanded={showOther} data-testid="desk-picker-other">
                                <ListItemText
                                    primary={`Other desks (${other.desks.length})`}
                                    secondary={showOther ? 'Hide them' : 'Handsets not shared with you'}
                                    primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                                />
                            </ListItemButton>
                        )}
                    </List>
                )}
            </DialogContent>
            <DialogActions>
                {heldDeskId && onRelease && (
                    <Button color="inherit" onClick={onRelease} sx={{ mr: 'auto' }} data-testid="desk-release">Release desk</Button>
                )}
                <Button onClick={onClose}>Cancel</Button>
            </DialogActions>
        </Dialog>
    );
}

DeskPickerDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    desks: PropTypes.array.isRequired,
    currentDeskId: PropTypes.string,
    // The desk this user holds now (server), offered for release.
    heldDeskId: PropTypes.string,
    // The user's admin-set usual desk, listed first (after the last-used one).
    usualDeskId: PropTypes.string,
    // The handset last used on this browser, listed first and marked.
    lastDeskId: PropTypes.string,
    // The user can start calls from the CRM: show which desks can't be rung yet (hint only).
    showCallSetup: PropTypes.bool,
    onPick: PropTypes.func.isRequired,
    onRelease: PropTypes.func,
    onClose: PropTypes.func.isRequired,
};

export function DeskClashDialog({ open, desk, heldBy, onTakeOver, onPickAnother, onClose }) {
    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth sx={dialogSx}>
            <DialogTitle>{deskLabel(desk)} is in use by {heldByName(heldBy)}</DialogTitle>
            <DialogContent>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Taking over moves the desk to this computer: its calls pop up for you instead. {heldByName(heldBy)} won&apos;t get them until they pick it again.
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={onPickAnother}>Pick another</Button>
                <Button variant="contained" onClick={onTakeOver} data-testid="desk-take-over">Take over</Button>
            </DialogActions>
        </Dialog>
    );
}

DeskClashDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    desk: PropTypes.object,
    heldBy: PropTypes.object,
    onTakeOver: PropTypes.func.isRequired,
    onPickAnother: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
};

export function HandsetFallbackDialog({ open, number, desk, reason, onClose }) {
    const [copied, setCopied] = useState(false);
    const copy = async () => {
        try { await navigator.clipboard.writeText(number); setCopied(true); } catch { /* clipboard blocked */ }
    };
    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth sx={dialogSx}>
            <DialogTitle>Dial {number} on your handset</DialogTitle>
            <DialogContent>
                <Stack spacing={1.5}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {reason === 'needs_reconnecting' && `${desk ? deskLabel(desk) : 'This desk'} needs reconnecting, `}
                        {reason === 'no_desk' && 'No desk is picked for this computer, '}
                        {reason !== 'needs_reconnecting' && reason !== 'no_desk' && `${desk ? deskLabel(desk) : 'This desk'} isn't connected to the CRM yet, `}
                        so the CRM can&apos;t ring it for you. Dial the number on the desk phone instead; the call still shows in Call Logs.
                    </Typography>
                    <Typography variant="body2" data-testid="handset-ask-admin">
                        Ask an admin to connect this desk in Settings → Phone system → Desks, so the Call button can ring it.
                    </Typography>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={copy} disabled={!number}>{copied ? 'Copied' : 'Copy number'}</Button>
                <Button variant="contained" onClick={onClose}>Done</Button>
            </DialogActions>
        </Dialog>
    );
}

HandsetFallbackDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    number: PropTypes.string,
    desk: PropTypes.object,
    reason: PropTypes.string,
    onClose: PropTypes.func.isRequired,
};
